<template>
  <div data-pc-key="none" :class="getClass()" @dragover.stop="onDragOver">
    <div data-pc-key="root" data-position="in" @dragstart.stop="onDragStart" @dragleave.stop="onDragLeave" @dragend.stop="onDragEnd" @drop.stop="onDrop" @click.stop="onClick" title="将左侧的组件拖拽到这里">
      <component v-for="c in children" :key="c.key" :is="c.configComponent" :endpoint="endpoint" :group="group" :config="c" ref="childComponents" @remove="removeChild"></component>
    </div>
  </div>
</template>

<script>
import mixin from './mixin.js'
import { canDrop, getConfigApi, getComponentKey } from '../util.js'
import { isArray } from '@/util.js'

export default {
  name: 'root-component',
  mixins: [mixin],
  props: {
    endpoint: { type: String, required: true },
    device: { type: String, required: false },
    group: { type: String, required: true }
  },
  data () {
    return {
      dc: null
    }
  },
  methods: {
    findComponent (key) {
      if (key === 'root') {
        return this
      }

      function find(components) {
        if (!components) {
          return null
        }
        if (!isArray(components)) {
          components = [components]
        }
        for (let component of components) {
          if (component.config.key === key) {
            return component
          }

          let temp = find(component.getChildComponents())
          if (temp) {
            return temp
          }
        }
        return null
      }

      return find(this.getChildComponents())
    },
    onDragStart (e) {
      let dragComponentKey = getComponentKey(e.target)
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('data', JSON.stringify({
        name: this.findComponent(dragComponentKey).getConfigName(),
        key: dragComponentKey
      }))
    },
    onDragOver (e) {
      e.preventDefault()

      let component = this.findComponent(getComponentKey(e.target))
      if (this.dc) {
        if (this.dc === component) {
          component.changeDragOver(e.target.getAttribute('data-position'))
        } else {
          this.dc.changeDragOver(false)
        }
      }
      this.dc = component
    },
    onDragLeave (e) {
      if (this === this.findComponent(getComponentKey(e.target))) {
        if (this.dc) {
          this.dc.changeDragOver(false)
        }
      }
    },
    onDragEnd (e) {
      e.dataTransfer.clearData('data')
    },
    onDrop (e) {
      if (this.dc) {
        this.dc.changeDragOver(false)
      }

      let dragComponentData = JSON.parse(e.dataTransfer.getData('data'))
      let dragComponentKey = dragComponentData.key
      let dropComponentKey = getComponentKey(e.target)
      if (dragComponentKey === dropComponentKey) {
        return
      }

      let dragComponentName = dragComponentData.name
      let dropComponent = this.findComponent(dropComponentKey)
      let position = e.target.getAttribute('data-position') || 'in'
      if (position === 'none') {
        return
      }
      if (position === 'in') {
        if (!canDrop(this.endpoint, this.group, dragComponentName, dropComponent.getConfigName())) {
          return
        }

        let child
        if (dragComponentKey) {
          let dragComponent = this.findComponent(dragComponentKey)
          if (dropComponent.isDescendant(dragComponent)) {
            return
          }
          child = dragComponent.getParentComponent().removeChild(dragComponent)
        } else {
          child = getConfigApi(this.endpoint, this.group, dragComponentName)
        }

        dropComponent.addChild(child, undefined, e)
      } else {
        let dropParentComponent = dropComponent.getParentComponent()
        if (!canDrop(this.endpoint, this.group, dragComponentName, dropParentComponent.getConfigName())) {
          return
        }

        let dropIndex
        let children = dropParentComponent.getChildren()
        for (let i = 0; i < children.length; i++) {
          if (children[i].key === dropComponent.config.key) {
            dropIndex = i
            break
          }
        }

        let child
        if (dragComponentKey) {
          let dragComponent = this.findComponent(dragComponentKey)
          if (dropParentComponent.isDescendant(dragComponent)) {
            return
          }

          let dragParentComponent = dragComponent.getParentComponent()
          if (dragParentComponent === dropParentComponent) {
            let dragIndex
            let children = dragParentComponent.getChildren()
            for (let i = 0; i < children.length; i++) {
              if (children[i].key === dragComponent.config.key) {
                dragIndex = i
                break
              }
            }

            if (position === 'before') {
              if (dragIndex === dropIndex - 1) {
                return
              }
            } else {
              if (dragIndex === dropIndex + 1) {
                return
              }
            }

            if (dragIndex < dropIndex) {
              dropIndex = dropIndex - 1
            }
            child = dragParentComponent.removeChild(dragComponent)
          } else {
            child = dragParentComponent.removeChild(dragComponent)
          }
        } else {
          child = getConfigApi(this.endpoint, this.group, dragComponentName)
        }

        if (position === 'before') {
          dropParentComponent.addChild(child, dropIndex, e)
        } else {
          dropParentComponent.addChild(child, dropIndex + 1, e)
        }
      }
    },
    onClick (e) {
      let component = this.findComponent(getComponentKey(e.target))
      if (component === this) {
        return
      }
      this.$emit('select', component)
    },
    getConfig (getValueType) {
      let idMap = {}
      let children = []
      let childComponents = this.getChildComponents()
      for (let childComponent of childComponents) {
        children.push(childComponent.getConfig(getValueType, idMap))
      }
      return children
    },
    getClass () {
      let clazz = 'pcd-root'
      if (this.device === 'MOBILE') {
        clazz += ' pcd-mobile'
      }
      if (this.dragOverPosition) {
        clazz += ` pcd-component-dragover-${this.dragOverPosition}`
      }
      return clazz
    }
  }
}
</script>

<style>
.pcd-root{padding:0.1px 0;}
.pcd-root>div{margin:10px 0;min-height:600px;border:1px dashed #f1f1f1;background:#f1f1f1;border-radius:4px;box-sizing:border-box;user-select:none;position:relative;}
.pcd-mobile>div{width:360px;min-height:640px;margin:10px auto;}
.pcd-root.pcd-component-dragover-in>div{border-color:red;}
.pcd-component{border:1px solid #ccc;margin:6px;cursor:pointer;}
.pcd-component-inline{display:inline-block;}
.pcd-component-dragover-before{border-left-color:red;border-top-color:red;}
.pcd-component-dragover-in{background:#FFF;}
.pcd-component-dragover-after{border-bottom-color:red;border-right-color:red;}
.pcd-component-selected{border-color:#409EFF;}
</style>